import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './styles.module.css';
import { Backdrop, Fade, IconButton, Modal, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomableImage, { DEFAULT_TRANSFORM } from './ZoomableImage';
import EvolutionCompareSlider from './EvolutionCompareSlider';
import EvolutionCompareControls, { COMPARE_MODES } from './EvolutionCompareControls';
import { describeElapsed, formatPhotoDateLong, isSliderCompatible, photoLabel } from '../../modules/evolutionPhotos';

const SIDES = Object.freeze([
  { slot: 'before', label: 'Antes' },
  { slot: 'after', label: 'Depois' },
]);

/** Tela cheia do navegador quando disponível; falha em silêncio (iPhone não tem). */
function enterBrowserFullscreen() {
  const root = document.documentElement;
  if (root.requestFullscreen && !document.fullscreenElement) {
    root.requestFullscreen().catch(() => {});
  }
}

function exitBrowserFullscreen() {
  if (document.fullscreenElement && document.exitFullscreen) {
    document.exitFullscreen().catch(() => {});
  }
}

/**
 * Modo apresentação: antes e depois em tela cheia, para mostrar ao paciente.
 * Fundo escuro, cada lado com a etiqueta e a data por extenso, o tempo
 * decorrido entre as duas fotos em destaque e os mesmos modos do card (lado a
 * lado ou deslizante) com zoom próprio. Esc, o X ou sair da tela cheia do
 * navegador fecham.
 */
const EvolutionPhotoPresentation = ({ before, after, initialMode, onClose }) => {
  const compatible = isSliderCompatible(before, after);
  const [mode, setMode] = useState(compatible ? initialMode : COMPARE_MODES.SIDE);
  const [transform, setTransform] = useState(DEFAULT_TRANSFORM);

  const handleTransform = useCallback((next) => {
    setTransform((current) => (typeof next === 'function' ? next(current) : next));
  }, []);

  // `onClose` muda de identidade a cada render do pai; o efeito da tela cheia
  // precisa rodar uma vez só (refazê-lo sairia da tela cheia e fecharia tudo)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    let entered = false;
    enterBrowserFullscreen();
    // Esc dentro da tela cheia do navegador sai dela sem chegar ao Modal: acompanha e
    // fecha junto, mas só depois de ter entrado de fato
    const handleChange = () => {
      if (document.fullscreenElement) {
        entered = true;
      } else if (entered) {
        onCloseRef.current();
      }
    };
    document.addEventListener('fullscreenchange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      exitBrowserFullscreen();
    };
  }, []);

  const photoFor = (slot) => (slot === 'before' ? before : after);
  const elapsed = describeElapsed(before, after);
  const subject = after && photoLabel(after) === photoLabel(before) ? photoLabel(before) : '';

  function renderSide({ slot, label }) {
    const photo = photoFor(slot);
    return (
      <div key={slot} className={styles.presentationSide}>
        <div className={styles.presentationHead}>
          <span className={styles.presentationBadge}>{label}</span>
          {photo ? (
            <>
              <span className={styles.presentationDate}>{formatPhotoDateLong(photo) || 'Sem data'}</span>
              {!subject && <span className={styles.presentationLabel}>{photoLabel(photo)}</span>}
            </>
          ) : (
            <span className={styles.presentationLabel}>Sem foto</span>
          )}
        </div>
        {photo ? (
          <ZoomableImage attachment={photo} transform={transform} onTransform={handleTransform} />
        ) : (
          <div className={`${styles.stage} ${styles.presentationEmpty}`}>Adicione outra foto para comparar</div>
        )}
      </div>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
      sx={{ zIndex: 1500 }}
    >
      <Fade in>
        <div className={styles.presentation} role="dialog" aria-label="Antes e depois em tela cheia">
          <div className={styles.presentationBar}>
            <div className={styles.presentationTitle}>
              <span className={styles.presentationTitleMain}>Antes e depois</span>
              {subject && <span className={styles.presentationTitleSub}>{subject}</span>}
            </div>

            <div className={styles.presentationControls}>
              <EvolutionCompareControls
                mode={mode}
                compatible={compatible}
                hasPair={Boolean(after)}
                onMode={setMode}
                transform={transform}
                onTransform={handleTransform}
              />
            </div>

            <Tooltip title="Fechar (Esc)">
              <IconButton onClick={onClose} className={styles.presentationClose} aria-label="Fechar tela cheia">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div className={styles.presentationBody}>
            {mode === COMPARE_MODES.SLIDER ? (
              <div className={styles.presentationSlider}>
                <EvolutionCompareSlider before={before} after={after} transform={transform} onTransform={handleTransform} />
              </div>
            ) : (
              <div className={styles.presentationSides}>{SIDES.map(renderSide)}</div>
            )}
          </div>

          {elapsed && (
            <div className={styles.presentationFoot}>
              <span className={styles.presentationElapsed}>{elapsed}</span>
            </div>
          )}
        </div>
      </Fade>
    </Modal>
  );
};

export default EvolutionPhotoPresentation;
